// HR Action Center settings — turns the opt-in Phase 2–4 signal sources on per tenant. Two config
// homes: attendance thresholds live on the attendance policy (leave-service); review-followup +
// onboarding live in hr_action_config (performance-service). All default off/conservative, so a
// tenant sees nothing until they enable it here. HR-gated (performance:manage).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Save, Clock, ClipboardCheck, UserPlus, CalendarClock } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import * as perf from '@/services/performanceService';
import * as att from '@/services/attendanceService';

export const HRActionSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Attendance (leave-service)
  const [attEnabled, setAttEnabled] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const [lateN, setLateN] = useState(3);
  const [absentN, setAbsentN] = useState(3);

  // Review follow-up + onboarding (performance-service)
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [reviewMax, setReviewMax] = useState(2.5);
  const [onboardEnabled, setOnboardEnabled] = useState(false);
  const [onboardDays, setOnboardDays] = useState(30);

  useEffect(() => {
    Promise.all([perf.getHRConfig(), att.getPolicy()])
      .then(([cfg, pol]) => {
        setReviewEnabled(cfg.review_followup_rating_max != null);
        if (cfg.review_followup_rating_max != null) setReviewMax(cfg.review_followup_rating_max);
        setOnboardEnabled(!!cfg.onboarding_checkin_enabled);
        setOnboardDays(cfg.onboarding_checkin_days ?? 30);
        setAttEnabled(pol.intervention_enabled ?? true);
        setWindowDays(pol.intervention_window_days ?? 30);
        setLateN(pol.lateness_review_threshold ?? 3);
        setAbsentN(pol.absence_review_threshold ?? 3);
      })
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load settings')))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        perf.saveHRConfig({
          review_followup_rating_max: reviewEnabled ? reviewMax : null,
          onboarding_checkin_enabled: onboardEnabled,
          onboarding_checkin_days: onboardDays,
        }),
        att.updatePolicy({
          intervention_enabled: attEnabled,
          intervention_window_days: windowDays,
          lateness_review_threshold: lateN,
          absence_review_threshold: absentN,
        }),
      ]);
      toast.success('HR Action Center settings saved');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Could not save settings')); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 24 }}><Loader2 className="animate-spin" /></div>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-charcoal">HR Action Center settings</h1>
      <p className="mb-5 mt-1 text-sm text-muted-foreground">
        Choose which situations automatically open an HR action. Everything is off or conservative by
        default — turn on what fits your policy. Thresholds are yours, never a platform default.
      </p>

      <Section icon={<Clock size={16} />} title="Repeated lateness & absence" subtitle="Aggregates recorded attendance; opens a manager-owned action when someone crosses your threshold.">
        <Toggle checked={attEnabled} onChange={setAttEnabled} label="Open attendance actions automatically" />
        {attEnabled && (
          <div style={grid}>
            <Num label="Look-back window (days)" value={windowDays} min={7} max={180} onChange={setWindowDays} />
            <Num label="Late days → action" value={lateN} min={1} max={60} onChange={setLateN} />
            <Num label="Unscheduled absences → action" value={absentN} min={1} max={60} onChange={setAbsentN} />
          </div>
        )}
      </Section>

      <Section icon={<ClipboardCheck size={16} />} title="Performance review follow-up" subtitle="When a finalized review is at or below this rating, open a follow-up action (the response — coaching/PIP — never another score).">
        <Toggle checked={reviewEnabled} onChange={setReviewEnabled} label="Open a follow-up on low review ratings" />
        {reviewEnabled && (
          <div style={grid}>
            <Num label="Rating at or below" value={reviewMax} min={0} max={10} step={0.1} onChange={setReviewMax} />
          </div>
        )}
      </Section>

      <Section icon={<UserPlus size={16} />} title="New-hire check-in" subtitle="Open a manager check-in a set number of days after a new hire starts.">
        <Toggle checked={onboardEnabled} onChange={setOnboardEnabled} label="Open onboarding check-ins automatically" />
        {onboardEnabled && (
          <div style={grid}>
            <Num label="Check-in after (days)" value={onboardDays} min={1} max={180} onChange={setOnboardDays} />
          </div>
        )}
      </Section>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
        <Button disabled={saving} onClick={save}>{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save settings</Button>
        <span style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
          <CalendarClock size={13} /> Escalation runs daily — overdue actions surface to HR automatically.
        </span>
      </div>
    </div>
  );
};

const Section: React.FC<{ icon: React.ReactNode; title: string; subtitle: string; children: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
  <Card style={{ marginBottom: 14 }}>
    <CardContent style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>{icon} {title}</div>
      <p style={{ fontSize: 12.5, color: '#64748b', margin: '4px 0 12px' }}>{subtitle}</p>
      {children}
    </CardContent>
  </Card>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
  </label>
);

const Num: React.FC<{ label: string; value: number; min?: number; max?: number; step?: number; onChange: (v: number) => void }> = ({ label, value, min, max, step, onChange }) => (
  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
    {label}
    <input type="number" value={value} min={min} max={max} step={step ?? 1}
      onChange={(e) => onChange(step ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
      className="w-[130px] rounded-lg border border-border bg-card px-2.5 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400" />
  </label>
);

const grid: React.CSSProperties = { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 };

export default HRActionSettings;
